import React, { useEffect, useState } from "react";
import { Download, PlugZap, Printer, Save, Smartphone } from "lucide-react";
import {
    downloadBridgeApk,
    fetchBridgeApkInfo,
    fetchPrinterSettings,
    printTestReceipt,
    savePrinterSettings,
    testPrinterConnection,
} from "../../services/printerApi";
import { fetchBranches } from "../../services/branchApi";
import { fetchCustomers } from "../../services/customerApi";
import { authStorage } from "../../utils/authStorage";
import ReactSelectField from "../CommonComponents/ReactSelectField";
import { resolveApiUrl } from "../../utils/apiBaseUrl";

const defaultForm = {
    connection_type: "ANDROID_BRIDGE",
    host: "192.168.254.200",
    port: 9100,
    timeout: 3000,
    paper_width: 80,
    auto_cut: true,
    cash_drawer_enabled: false,
    open_drawer_on_cash: true,
    receipt_logo_enabled: false,
    receipt_logo_size: "LARGE",
    receipt_logo_url: null,
};

export default function HardwarePrinterSection() {
    const [form, setForm] = useState(defaultForm);
    const [customers, setCustomers] = useState([]);
    const [branches, setBranches] = useState([]);
    const currentUser = authStorage.getUser();
    const isSuperAdmin = Number(currentUser?.user_type) === 0;
    const [selectedCustomerId, setSelectedCustomerId] = useState(isSuperAdmin ? "" : authStorage.getCustomerId() || "");
    const [selectedBranchId, setSelectedBranchId] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [bridgeInfo, setBridgeInfo] = useState(null);
    const [bridgeLoading, setBridgeLoading] = useState(true);
    const [bridgeDownloading, setBridgeDownloading] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreviewUrl, setLogoPreviewUrl] = useState("");
    const [removeLogo, setRemoveLogo] = useState(false);

    useEffect(() => {
        if (!logoFile) {
            setLogoPreviewUrl("");
            return;
        }
        const url = URL.createObjectURL(logoFile);
        setLogoPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [logoFile]);

    useEffect(() => {
        setLogoFile(null);
        setRemoveLogo(false);
    }, [selectedCustomerId, selectedBranchId]);

    const customerOptions = customers.map((customer) => ({
        value: String(customer.id),
        label: customer.name || customer.customer_name || `Customer #${customer.id}`,
    }));
    const activeBranches = branches.filter((branch) => String(branch?.status || "").toUpperCase() === "ACTIVE");
    const branchOptions = [
        { value: "", label: "Company default printer" },
        ...activeBranches.map((branch) => ({
            value: String(branch.id),
            label: branch.name || branch.location || `Branch #${branch.id}`,
        })),
    ];
    const paperWidthOptions = [
        { value: 80, label: "80mm" },
        { value: 58, label: "58mm" },
    ];
    const connectionTypeOptions = [
        { value: "ANDROID_BRIDGE", label: "Android Print Bridge" },
        { value: "NETWORK_ESCPOS", label: "Direct Network ESC/POS" },
    ];

    useEffect(() => {
        if (!isSuperAdmin) return;
        let mounted = true;

        fetchCustomers()
            .then((response) => {
                if (!mounted) return;
                const customerRows = Array.isArray(response.data) ? response.data : response.data?.data || [];
                setCustomers(customerRows);
                setSelectedCustomerId((current) => current || customerRows[0]?.id || "");
            })
            .catch(() => {
                if (mounted) setError("Failed to load customers for printer setup.");
            });

        return () => {
            mounted = false;
        };
    }, [isSuperAdmin]);

    useEffect(() => {
        if (isSuperAdmin) {
            setBridgeLoading(false);
            return;
        }

        let mounted = true;
        setBridgeLoading(true);
        fetchBridgeApkInfo()
            .then((response) => {
                if (mounted) setBridgeInfo(response.data || null);
            })
            .catch(() => {
                if (mounted) setBridgeInfo(null);
            })
            .finally(() => {
                if (mounted) setBridgeLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, [isSuperAdmin]);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        const customerId = isSuperAdmin ? selectedCustomerId : authStorage.getCustomerId();

        if (!customerId) {
            setLoading(false);
            return () => {
                mounted = false;
            };
        }

        Promise.all([
            fetchPrinterSettings({ customerId, branchId: selectedBranchId }),
            customerId ? fetchBranches(customerId).catch(() => ({ data: { data: [] } })) : Promise.resolve({ data: { data: [] } }),
        ])
            .then(([settingsResponse, branchesResponse]) => {
                if (!mounted) return;
                const nextSettings = { ...defaultForm, ...(settingsResponse.data || {}), branch_id: selectedBranchId || null };
                setForm({
                    ...nextSettings,
                    connection_type: nextSettings.connection_type || "ANDROID_BRIDGE",
                });
                setBranches(Array.isArray(branchesResponse.data?.data) ? branchesResponse.data.data : []);
            })
            .catch((err) => {
                if (mounted) setError(err.response?.data?.message || "Failed to load printer settings.");
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, [isSuperAdmin, selectedCustomerId, selectedBranchId]);

    const scope = { customerId: isSuperAdmin ? selectedCustomerId : "", branchId: selectedBranchId };
    const hasPrinterScope = !isSuperAdmin || Boolean(selectedCustomerId);
    const busy = saving || testing || printing;
    const logoUrl = removeLogo ? "" : logoPreviewUrl || (form.receipt_logo_url ? resolveApiUrl(form.receipt_logo_url) : "");
    const logoScale = { SMALL: 0.5, MEDIUM: 0.75, LARGE: 1 }[form.receipt_logo_size] || 1;

    const handleLogoSelection = (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        if (!["image/png", "image/jpeg", "image/webp"].includes(file.type) || file.size > 20 * 1024 * 1024) {
            setError("Choose a PNG, JPG, or WebP logo up to 20 MB.");
            return;
        }
        setLogoFile(file);
        setRemoveLogo(false);
        updateField("receipt_logo_enabled", true);
    };

    const updateField = (key, value) => {
        setMessage("");
        setError("");
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const persistPrinterSettings = async () => {
        const response = await savePrinterSettings({
            ...form,
            connection_type: form.connection_type || "ANDROID_BRIDGE",
            branch_id: selectedBranchId || "default",
            ...(isSuperAdmin ? { customer_id: selectedCustomerId } : {}),
        }, logoFile, removeLogo);
        setLogoFile(null);
        setRemoveLogo(false);
        const savedConfig = { ...defaultForm, ...(response.data?.config || {}), branch_id: selectedBranchId || null };
        setForm({
            ...savedConfig,
            connection_type: savedConfig.connection_type || "ANDROID_BRIDGE",
        });
        return response;
    };

    const handleSave = async (event) => {
        event.preventDefault();
        setSaving(true);
        setMessage("");
        setError("");
        if (!hasPrinterScope) {
            setSaving(false);
            setError("Select a customer before saving printer settings.");
            return;
        }
        try {
            const response = await persistPrinterSettings();
            setMessage(response.data?.message || "Printer settings saved.");
        } catch (err) {
            setError(err.response?.data?.message || "Failed to save printer settings.");
        } finally {
            setSaving(false);
        }
    };

    const runAction = async (action, setBusy, fallbackMessage) => {
        setBusy(true);
        setMessage("");
        setError("");
        if (!hasPrinterScope) {
            setBusy(false);
            setError("Select a customer before using printer actions.");
            return;
        }
        try {
            await persistPrinterSettings();
            const response = await action();
            setMessage(response.data?.message || fallbackMessage);
        } catch (err) {
            setError(err.response?.data?.message || fallbackMessage);
        } finally {
            setBusy(false);
        }
    };

    const bridgeReportedAt = form.bridge_reported_at
        ? new Date(form.bridge_reported_at).toLocaleString()
        : null;
    const bridgeCandidates = Array.isArray(form.bridge_printer_candidates)
        ? form.bridge_printer_candidates
        : [];

    const handleDownloadBridge = async () => {
        setBridgeDownloading(true);
        setMessage("");
        setError("");
        try {
            const response = await downloadBridgeApk();
            const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: "application/vnd.android.package-archive" }));
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = bridgeInfo?.fileName || "zeroone-print-bridge.apk";
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
            setMessage("Print Bridge APK download started.");
        } catch (err) {
            setError(err.response?.data?.message || "Print Bridge APK is not available yet.");
        } finally {
            setBridgeDownloading(false);
        }
    };

    if (loading) {
        return (
            <div className="px-5 pb-6 sm:px-6">
                <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm font-semibold text-slate-500 shadow-sm">
                    Loading printer settings...
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSave} className="space-y-4 px-5 pb-6 sm:px-6">
            <fieldset disabled={busy} className="min-w-0 space-y-4">
            {!isSuperAdmin && (
                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-start gap-3">
                            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm" style={{ background: "var(--color-primary)" }}>
                                <Smartphone className="h-5 w-5" />
                            </span>
                            <div>
                                <h3 className="text-base font-bold text-slate-900">Android Print Bridge APK</h3>
                                <p className="mt-1 text-sm leading-6 text-slate-500">
                                    Install this on the store tablet or phone that stays on the same network as the receipt printer.
                                </p>
                                <div className="mt-2 flex flex-wrap gap-2 text-xs font-semibold">
                                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-600">
                                        {bridgeLoading ? "Checking APK..." : bridgeInfo?.version ? `Version ${bridgeInfo.version}` : "Version unavailable"}
                                    </span>
                                    <span className={`rounded-full px-2.5 py-1 ${bridgeInfo?.available ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                                        {bridgeInfo?.available ? "Ready to download" : "APK not uploaded yet"}
                                    </span>
                                    {bridgeInfo?.size_bytes > 0 && (
                                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-600">
                                            {(bridgeInfo.size_bytes / 1024 / 1024).toFixed(1)} MB
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={handleDownloadBridge}
                            disabled={bridgeLoading || bridgeDownloading || !bridgeInfo?.available}
                            className="inline-flex h-12 shrink-0 items-center justify-center gap-2 rounded-2xl px-4 text-sm font-bold text-white shadow-sm transition hover:opacity-90 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:opacity-70"
                            style={bridgeInfo?.available ? { background: "var(--color-primary)" } : undefined}
                        >
                            <Download className="h-4 w-4" />
                            {bridgeDownloading ? "Downloading..." : "Download APK"}
                        </button>
                    </div>
                </div>
            )}

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-start gap-3">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm" style={{ background: "var(--color-primary)" }}>
                        <Printer className="h-5 w-5" />
                    </span>
                    <div>
                        <h3 className="text-base font-bold text-slate-900">Receipt Printer</h3>
                        <p className="mt-1 text-sm text-slate-500">
                            Use Android Bridge for SaaS stores, or direct ESC/POS only when the backend is inside the same store network.
                        </p>
                    </div>
                </div>

                <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                            <p className="text-sm font-bold text-slate-900">Bridge Network Status</p>
                            <p className="mt-1 text-xs leading-5 text-slate-500">
                                Reported by the Android Print Bridge from inside the store network.
                            </p>
                        </div>
                        <span className={`w-fit rounded-full px-2.5 py-1 text-xs font-bold ${bridgeReportedAt ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                            {bridgeReportedAt ? "Reported" : "No report yet"}
                        </span>
                    </div>
                    <div className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
                        <div className="rounded-xl bg-white px-3 py-2 ring-1 ring-slate-200">
                            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Bridge Device IP</p>
                            <p className="mt-1 font-semibold text-slate-800">{form.bridge_device_ip || "-"}</p>
                        </div>
                        <div className="rounded-xl bg-white px-3 py-2 ring-1 ring-slate-200">
                            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Gateway</p>
                            <p className="mt-1 font-semibold text-slate-800">{form.bridge_gateway_ip || "-"}</p>
                        </div>
                        <div className="rounded-xl bg-white px-3 py-2 ring-1 ring-slate-200">
                            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Network Prefix</p>
                            <p className="mt-1 font-semibold text-slate-800">{form.bridge_network_prefix || "-"}</p>
                        </div>
                        <div className="rounded-xl bg-white px-3 py-2 ring-1 ring-slate-200">
                            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Last Report</p>
                            <p className="mt-1 font-semibold text-slate-800">{bridgeReportedAt || "-"}</p>
                        </div>
                    </div>
                    <div className="mt-3 rounded-xl bg-white px-3 py-2 ring-1 ring-slate-200">
                        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Detected Printer IPs</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {bridgeCandidates.length > 0 ? bridgeCandidates.map((candidate) => (
                                <span key={String(candidate.host || candidate.ip || candidate)} className="rounded-full bg-sky-50 px-2.5 py-1 text-xs font-bold text-sky-700">
                                    {candidate.host || candidate.ip || candidate}
                                </span>
                            )) : (
                                <span className="text-sm font-semibold text-slate-500">No detected printers yet.</span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                    {isSuperAdmin && (
                        <label className="block sm:col-span-2">
                            <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">Customer</span>
                            <ReactSelectField
                                options={customerOptions}
                                value={customerOptions.find((option) => option.value === String(selectedCustomerId)) || null}
                                onChange={(option) => {
                                    setSelectedCustomerId(option?.value || "");
                                    setSelectedBranchId("");
                                    setMessage("");
                                    setError("");
                                }}
                                placeholder="Select customer"
                                isDisabled={busy}
                                ariaLabel="Select customer"
                            />
                            <p className="mt-2 text-xs text-slate-500">
                                Superadmin printer setup is saved per customer, with optional branch overrides.
                            </p>
                        </label>
                    )}
                    <label className="block sm:col-span-2">
                        <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">Branch Scope</span>
                        <ReactSelectField
                            options={branchOptions}
                            value={branchOptions.find((option) => option.value === String(selectedBranchId)) || branchOptions[0]}
                            onChange={(option) => {
                                setSelectedBranchId(option?.value || "");
                                setMessage("");
                                setError("");
                            }}
                            placeholder="Select printer scope"
                            isDisabled={busy || (isSuperAdmin && !selectedCustomerId)}
                            ariaLabel="Select printer branch scope"
                        />
                        <p className="mt-2 text-xs text-slate-500">
                            Branch-specific settings override the company default for cashiers assigned to that branch.
                        </p>
                    </label>
                    <label className="block sm:col-span-2">
                        <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">Connection Mode</span>
                        <ReactSelectField
                            options={connectionTypeOptions}
                            value={connectionTypeOptions.find((option) => option.value === String(form.connection_type || "ANDROID_BRIDGE")) || connectionTypeOptions[0]}
                            onChange={(option) => updateField("connection_type", option?.value || "ANDROID_BRIDGE")}
                            placeholder="Select connection mode"
                            isDisabled={busy}
                            isSearchable={false}
                            ariaLabel="Select printer connection mode"
                        />
                        <p className="mt-2 text-xs text-slate-500">
                            Android Bridge queues print jobs to the phone/tablet. Direct ESC/POS makes the server connect to the printer IP itself.
                            Save settings before using Test Connection or Print Test.
                        </p>
                    </label>
                    <label className="block">
                        <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">Printer IP</span>
                        <input
                            value={form.host}
                            onChange={(event) => updateField("host", event.target.value)}
                            className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-800 outline-none transition focus:border-slate-400"
                            placeholder="192.168.254.200"
                            required
                        />
                    </label>
                    <label className="block">
                        <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">Port</span>
                        <input
                            type="number"
                            min="1"
                            max="65535"
                            value={form.port}
                            onChange={(event) => updateField("port", Number(event.target.value))}
                            className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-800 outline-none transition focus:border-slate-400"
                            required
                        />
                    </label>
                    <label className="block">
                        <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">Timeout MS</span>
                        <input
                            type="number"
                            min="500"
                            step="100"
                            value={form.timeout}
                            onChange={(event) => updateField("timeout", Number(event.target.value))}
                            className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-800 outline-none transition focus:border-slate-400"
                        />
                    </label>
                    <label className="block">
                        <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">Paper Width</span>
                        <ReactSelectField
                            options={paperWidthOptions}
                            value={paperWidthOptions.find((option) => option.value === Number(form.paper_width)) || paperWidthOptions[0]}
                            onChange={(option) => updateField("paper_width", Number(option?.value || 80))}
                            placeholder="Select paper width"
                            isDisabled={busy}
                            isSearchable={false}
                            ariaLabel="Select receipt paper width"
                        />
                    </label>
                </div>

                <div className="mt-5 grid gap-3">
                    {[
                        ["auto_cut", "Auto cut receipt"],
                        ["cash_drawer_enabled", "Cash drawer connected"],
                        ["open_drawer_on_cash", "Open drawer for cash payments"],
                    ].map(([key, label]) => (
                        <label key={key} className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                            <span className="text-sm font-semibold text-slate-700">{label}</span>
                            <input
                                type="checkbox"
                                checked={Boolean(form[key])}
                                onChange={(event) => updateField(key, event.target.checked)}
                                className="h-5 w-5 accent-[var(--color-primary)]"
                            />
                        </label>
                    ))}
                </div>
            </div>

            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" aria-labelledby="receipt-logo-heading">
                <h3 id="receipt-logo-heading" className="text-base font-bold text-slate-900">Receipt Logo</h3>
                <p className="mt-1 text-sm text-slate-500">Place your logo above the store name for the selected printer scope.</p>
                <div className="mt-4 grid gap-5 sm:grid-cols-2">
                    <div className="space-y-3">
                        {logoUrl && <img src={logoUrl} alt="Uploaded receipt logo" className="h-20 max-w-full rounded-lg border border-slate-200 bg-white p-2 object-contain" />}
                        <label className="block text-sm font-semibold text-slate-700">
                            {logoUrl ? "Replace Logo" : "Upload Logo"}
                            <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleLogoSelection}
                                disabled={!hasPrinterScope || busy}
                                className="mt-2 block w-full text-xs text-slate-500 file:mr-2 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:font-semibold file:text-slate-700" />
                        </label>
                        <p className="text-xs leading-5 text-slate-500">PNG, JPG, or WebP, up to 20 MB. Uploads are resized and saved as compressed WebP.</p>
                        {logoFile && <p className="break-all text-xs font-semibold text-slate-600">Selected: {logoFile.name}</p>}
                        {logoUrl && <button type="button" className="text-sm font-semibold text-rose-600" onClick={() => {
                            setLogoFile(null);
                            setRemoveLogo(true);
                            updateField("receipt_logo_enabled", false);
                        }}>Remove Logo</button>}
                        {(logoFile || removeLogo) && <p className="text-xs text-slate-500">Save Settings to apply this change. The old file is deleted when no branch uses it.</p>}
                        <label className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                            <input type="checkbox" checked={Boolean(form.receipt_logo_enabled)} disabled={!logoUrl || busy}
                                onChange={(event) => updateField("receipt_logo_enabled", event.target.checked)}
                                className="h-5 w-5 accent-[var(--color-primary)]" />
                            <span className="text-sm font-semibold text-slate-700">Show logo on receipts</span>
                        </label>
                        <p className="text-xs text-slate-500">Uncheck to hide the logo while keeping the uploaded file. Save Settings to apply.</p>
                        <fieldset disabled={busy || !hasPrinterScope}>
                            <legend className="mb-2 text-sm font-semibold text-slate-700">Logo size</legend>
                            <div className="grid grid-cols-3 gap-2">
                                {["SMALL", "MEDIUM", "LARGE"].map((size) => <button
                                    key={size} type="button" aria-pressed={form.receipt_logo_size === size}
                                    onClick={() => updateField("receipt_logo_size", size)}
                                    className="rounded-xl border border-slate-200 px-2 py-2 text-sm font-semibold disabled:opacity-50"
                                    style={form.receipt_logo_size === size ? { background: "var(--color-primary)", color: "#fff" } : { background: "#fff", color: "#334155" }}
                                >{size.charAt(0) + size.slice(1).toLowerCase()}</button>)}
                            </div>
                            <p className="mt-2 text-xs text-slate-500">Small 50%, Medium 75%, Large 100% of the original print size. Save Settings to apply.</p>
                        </fieldset>
                    </div>
                    <div>
                        <p className="mb-2 text-xs font-semibold text-slate-500">Receipt preview</p>
                        <div className="mx-auto max-w-[260px] border border-dashed border-slate-300 bg-white p-5 text-center font-mono text-xs text-black">
                            {form.receipt_logo_enabled && logoUrl && <img src={logoUrl} alt="Logo on receipt preview" className="mx-auto mb-3 object-contain grayscale" style={{ maxHeight: `${112 * logoScale}px`, maxWidth: `${100 * logoScale}%` }} />}
                            <p className="font-bold">STORE NAME</p>
                            <p className="mt-1">Branch / address</p>
                            <div className="my-3 border-t border-dashed border-slate-400" />
                            <p>RECEIPT</p>
                            <div className="mt-3 flex justify-between"><span>Item</span><span>100.00</span></div>
                            <div className="mt-3 flex justify-between font-bold"><span>TOTAL</span><span>100.00</span></div>
                        </div>
                        <p className="mt-2 text-xs text-slate-500">Use Print Test to check the result on your receipt printer.</p>
                    </div>
                </div>
            </section>

            {message && <div role="status" className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">{message}</div>}
            {error && <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">{error}</div>}

            <div className="grid gap-3 sm:grid-cols-3">
                <button
                    type="button"
                    onClick={() => runAction(() => testPrinterConnection(scope), setTesting, "Connection tested.")}
                    disabled={!hasPrinterScope || testing || saving || printing}
                    className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-wait disabled:opacity-60"
                >
                    <PlugZap className="h-4 w-4" />
                    {testing ? "Testing..." : "Test Connection"}
                </button>
                <button
                    type="button"
                    onClick={() => runAction(() => printTestReceipt(scope), setPrinting, "Test receipt sent.")}
                    disabled={!hasPrinterScope || testing || saving || printing}
                    className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-wait disabled:opacity-60"
                >
                    <Printer className="h-4 w-4" />
                    {printing ? "Printing..." : "Print Test"}
                </button>
                <button
                    type="submit"
                    disabled={!hasPrinterScope || saving || testing || printing}
                    className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl px-4 text-sm font-bold text-white shadow-sm transition hover:opacity-90 disabled:cursor-wait disabled:opacity-60"
                    style={{ background: "var(--color-primary)" }}
                >
                    <Save className="h-4 w-4" />
                    {saving ? "Saving..." : "Save Settings"}
                </button>
            </div>
            </fieldset>
        </form>
    );
}
